import { useEffect, useState } from "react";
import { SchemaData, UseGetItemProps } from "./types";
import { ApiCMSRequest } from "data/ApiCMS";
import schemaJson from "../schema/SchemaJson.json";
import UISchemaJson from "../schema/eventUISchema";
import { GET_ITEM_QUERY, GetItemGql } from "../queries/getItem";
import { removeNullValues } from "utils/removeNullValues";
import useEventsRelated from "./useEventsRelated";
import { formatDateTimeForForm } from "./dateUtils";
import { parseInternalContentJSON } from "./internalContentUtils";

type ProgramationJSON = {
  items: unknown[];
  [key: string]: unknown;
};

const emptyProgramationJSON: ProgramationJSON = { items: [] };

const parseProgramationJSON = (programationJSON?: string | null): ProgramationJSON => {
  if (!programationJSON) return { ...emptyProgramationJSON };

  try {
    const parsed = JSON.parse(programationJSON);

    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      return { items: [], ...parsed };
    }
  } catch (error) {
    console.warn("Invalid event programationJSON", error);
  }

  return { ...emptyProgramationJSON };
};

const useGetItem = ({ setState, setTemplate, queryParam }: UseGetItemProps<SchemaData>) => {
  const { eventsRelated, getEventsRelated } = useEventsRelated();
  const [responseData, setResponseData] = useState<Omit<GetItemGql["Event"], "internalContentJSON"> & { internalContentJSON?: string }>();

  useEffect(() => {
    if (!!queryParam) {
      setState((props) => ({ ...props, messageError: undefined, criticalError: undefined }));
      ApiCMSRequest<GetItemGql>({
        query: GET_ITEM_QUERY,
        variables: { id: queryParam },
      })
        .then((response) => {
          if (response?.data?.errors?.length) {
            throw new Error(
              response.data.errors.map((error) => error.message).filter(Boolean).join("; ") ||
                "Não foi possível carregar o conteúdo interno do evento.",
              { cause: { response: response.data } }
            );
          }

          if (!response?.data?.data?.Event) {
            throw new Error("Não encontrado", {
              cause: { response: response?.data || response },
            });
          }

          const data = {
            ...response.data.data?.Event,
            internalContentJSON: response.data.data?.AdminEventInternalContent,
            relatedPostsIds: response.data.data.AdminEventRelatedPosts.map(({ id }) => id),
          };

          setResponseData(data);
          getEventsRelated({ slug: data.slug });
        })
        .catch((error) => {
          console.log({ error });

          setState((prev) => ({
            ...prev,
            criticalError: error?.response?.data?.errors?.[0] || error,
          }));
        });
    }
  }, [queryParam]);

  useEffect(() => {
    if (!responseData || !eventsRelated) return;

    setTemplate({
      schemaJson: schemaJson,
      UISchema: UISchemaJson,
    });

    const { mediaFileDesktop, mediaFileMobile, programationJSON, internalContentJSON, dateFinish, dateInit, ...data } =
      responseData;

    try {
      const newData = removeNullValues({
        ...data,
        dateInit: formatDateTimeForForm(dateInit, data.hourInit),
        dateFinish: formatDateTimeForForm(dateFinish, "23:59"),
        idMediaFileDesktop: mediaFileDesktop.id,
        idMediaFileMobile: mediaFileMobile.id,
        imageUrl: mediaFileDesktop.url,
        programationJSON: parseProgramationJSON(programationJSON),
        internalContentJSON: parseInternalContentJSON(internalContentJSON),
        relatedEventsIds: eventsRelated,
        relatedPostsIds: responseData.relatedPostsIds,
      });

      setState({ data: newData });
    } catch (error: any) {
      setState((previous) => ({
        ...previous,
        criticalError: error instanceof Error
          ? error
          : new Error("Não foi possível interpretar o conteúdo interno do evento."),
      }));
    }
  }, [responseData, eventsRelated]);
};

export default useGetItem;
